/**
 * Airflow filters negative controls per pool, so a pool *is* the mapping from samples to the controls they
 * are judged against. Pools are therefore told apart by their control sets: every pool's set must differ
 * from every other's, and at most one pool may have none. A control on a shared plate can sit in several
 * pools, so sets are compared, not checked for disjointness.
 */

export type ControlGroup = { key: string; controls: Iterable<string> }

export type ControlRuleResult = {
  holds: boolean
  /** Keys of the groups without any control; more than one breaks the rule. */
  withoutControls: string[]
  /** Groups whose control sets are identical, each list naming one set's groups. */
  identicalSets: string[][]
}

const setKey = (controls: Iterable<string>) => [...new Set(controls)].sort().join('\u0000')

export const checkControlRule = (groups: readonly ControlGroup[]): ControlRuleResult => {
  const bySet = new Map<string, string[]>()
  const withoutControls: string[] = []
  groups.forEach((group) => {
    const key = setKey(group.controls)
    if (key === '') {
      withoutControls.push(group.key)
      return
    }
    bySet.set(key, [...(bySet.get(key) ?? []), group.key])
  })
  const identicalSets = [...bySet.values()].filter((keys) => keys.length > 1)
  return { holds: withoutControls.length <= 1 && identicalSets.length === 0, withoutControls, identicalSets }
}
